import SubconfigurationRow, {
  SubconfigurationRowStyled,
} from '../../ConfigurationCategories/SegmentPotentialDefinition/components/components';
import { OpenIcon, Row, Title } from '@ydistri/ds';
import React, { PropsWithChildren } from 'react';
import { styled } from 'styled-components';
import { useConfigurationSectionOpenState } from '../../../../hooks/useConfigurationSectionOpenState';

interface ConfigurationSectionProps extends PropsWithChildren {
  id: string;
  title: string;
  collapsedTitleAdditionalContent?: React.ReactNode;
}

const TitleWrapper = styled.button.attrs({ type: 'button' })`
  flex-grow: 1;
  cursor: pointer;
  background: none;
  border: 0;
  padding: 0;
  text-align: left;
`;

const SubconfigurationTitleRow = styled(SubconfigurationRowStyled)`
  padding-left: 0.5rem;
`;

interface ConfigurationSectionTitleProps {
  title: string;
  onClick?: () => void;
}

/**
 * Stretches the title to the full width and makes the whole row clickable to open/close the section content
 * @param title Title to display
 * @param onClick event handler for the row click
 * @constructor
 */
const ConfigurationSectionTitle: React.FC<ConfigurationSectionTitleProps> = ({
  title,
  onClick,
}) => {
  return (
    <TitleWrapper onClick={onClick} title="Hide/Show content">
      <Title>{title}</Title>
    </TitleWrapper>
  );
};

/**
 * Standalone section in the configuration pane with a title and open/close button to show/hide the
 * content.
 * The section's open/closed state is saved to localStorage on project's level so the section
 * is displayed in a state the user left it. When a section is used for project A and not for
 * project B, it will be displayed only when needed.
 * @param id Identification of the section
 * @param title Title to display
 * @param children children to display when section is open
 */
const ConfigurationSection: React.FC<ConfigurationSectionProps> = ({
  title,
  id,
  collapsedTitleAdditionalContent,
  children,
}) => {
  const { contentDisplayed, toggleOpenHandler } = useConfigurationSectionOpenState(id);
  return (
    <>
      <SubconfigurationRow start />
      <SubconfigurationTitleRow>
        <Row $gap="0.5rem" $alignItems="center" id={id}>
          <OpenIcon $isOpened={contentDisplayed} $canBeOpened={true} onClick={toggleOpenHandler} />
          <ConfigurationSectionTitle title={title} onClick={toggleOpenHandler} />
          {!contentDisplayed && collapsedTitleAdditionalContent}
        </Row>
      </SubconfigurationTitleRow>
      {contentDisplayed && children}
      <SubconfigurationRow end />
    </>
  );
};

export default ConfigurationSection;
